import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, Download, Pencil, X } from 'lucide-react';
import {
  LIST_DESCRIPTION_MAX_LENGTH,
  LIST_NAME_MAX_LENGTH,
} from '@translate-ai/shared';
import { BackButton } from '@/components/BackButton';
import { ErrorMessage } from '@/components/ErrorMessage';
import { HistoryItemCard } from '@/components/HistoryItemCard';
import { LoadingPlaceholder } from '@/components/LoadingPlaceholder';
import {
  downloadHistoryExport,
  useDeleteTranslation,
  useListDetail,
  useRemoveItemFromList,
  useToggleFavorite,
  useUpdateList,
} from '@/api/hooks';
import { ApiError } from '@/api/client';

export function ListDetail() {
  const { listId } = useParams<{ listId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const detail = useListDetail(listId, 20);
  const update = useUpdateList();
  const remove = useRemoveItemFromList();
  const del = useDeleteTranslation();
  const fav = useToggleFavorite();

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  const [showExport, setShowExport] = useState(false);

  const list = detail.data?.pages[0]?.list;
  const items = detail.data?.pages.flatMap((p) => p.items) ?? [];
  const isFavorites = list?.isSystem && list.systemKey === 'favorites';
  const localizedName = isFavorites ? t('lists.favorites') : list?.name ?? '';

  useEffect(() => {
    if (list && !editing) {
      setName(list.name);
      setDescription(list.description ?? '');
    }
  }, [list, editing]);

  const handleSave = async () => {
    if (!list) return;
    setEditError(null);
    try {
      await update.mutateAsync({
        id: list.id,
        name: name.trim(),
        description: description.trim() || null,
      });
      setEditing(false);
    } catch (err) {
      setEditError(err instanceof ApiError ? err.message : t('errors.generic'));
    }
  };

  return (
    <div className="space-y-3 py-2">
      <div className="flex items-center justify-between">
        <BackButton onClick={() => navigate(-1)} />
        {list && !editing && (
          <div className="flex shrink-0 gap-2">
            <button
              className="btn-secondary h-8 px-2"
              onClick={() => setShowExport((s) => !s)}
              aria-label={showExport ? t('history.exportClose') : t('history.export')}
              title={showExport ? t('history.exportClose') : t('history.export')}
            >
              {showExport ? <X className="h-4 w-4" /> : <Download className="h-4 w-4" />}
            </button>
            {!list.isSystem && (
              <button
                className="btn-ghost h-8 px-2"
                onClick={() => setEditing(true)}
                aria-label={t('lists.editDescription')}
                title={t('lists.editDescription')}
              >
                <Pencil className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
      </div>

      {detail.isLoading && <LoadingPlaceholder />}

      {list && !editing && (
        <header className="space-y-1">
          <h1 className="text-xl font-semibold">{localizedName}</h1>
          {list.description && (
            <p className="text-sm text-slate-500">{list.description}</p>
          )}
          <p className="text-xs text-slate-400">
            {t('lists.itemsCount', { count: list.itemCount })}
          </p>
        </header>
      )}

      {list && showExport && !editing && (
        <div className="card flex gap-2 p-3">
          <button
            className="btn-secondary flex-1"
            onClick={() => {
              void downloadHistoryExport('json', list.id);
              setShowExport(false);
            }}
          >
            {t('history.exportJson')}
          </button>
          <button
            className="btn-secondary flex-1"
            onClick={() => {
              void downloadHistoryExport('txt', list.id);
              setShowExport(false);
            }}
          >
            {t('history.exportTxt')}
          </button>
        </div>
      )}

      {list && editing && (
        <div className="card space-y-2 p-3">
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={LIST_NAME_MAX_LENGTH}
            autoFocus
          />
          <textarea
            className="input min-h-[80px]"
            placeholder={t('lists.descriptionPlaceholder')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={LIST_DESCRIPTION_MAX_LENGTH}
          />
          <ErrorMessage message={editError} />
          <div className="flex justify-end gap-2">
            <button
              className="btn-ghost h-9 px-3 text-sm"
              onClick={() => {
                setEditing(false);
                setEditError(null);
              }}
            >
              <X className="h-4 w-4" />
              {t('lists.cancel')}
            </button>
            <button
              className="btn-primary h-9 px-3 text-sm"
              onClick={handleSave}
              disabled={update.isPending || !name.trim()}
            >
              <Check className="h-4 w-4" />
              {t('lists.save')}
            </button>
          </div>
        </div>
      )}

      {!detail.isLoading && items.length === 0 && (
        <p className="text-slate-500">{t('lists.emptyItems')}</p>
      )}

      <ul className="space-y-2">
        {items.map((item) => (
          <li key={item.id}>
            <HistoryItemCard
              item={item}
              onLoadIntoForm={() => navigate('/', { state: { useAsInput: item } })}
              onToggleFavorite={() =>
                fav.mutate({ translationId: item.id, isFavorite: !item.isFavorite })
              }
              onOpenLists={() => navigate(`/items/${item.id}/lists`)}
              onRemoveFromList={
                listId
                  ? () => {
                      if (window.confirm(t('lists.removeFromListConfirm'))) {
                        remove.mutate({ listId, translationId: item.id });
                      }
                    }
                  : undefined
              }
              onDelete={() => {
                if (window.confirm(t('lists.deleteTranslationConfirm'))) {
                  del.mutate(item.id);
                }
              }}
            />
          </li>
        ))}
      </ul>

      {detail.hasNextPage && (
        <button
          onClick={() => void detail.fetchNextPage()}
          disabled={detail.isFetchingNextPage}
          className="btn-secondary mt-4 w-full"
        >
          {t('history.loadMore')}
        </button>
      )}
    </div>
  );
}
